import {
  ColumnDef,
  flexRender,
  useReactTable,
  getCoreRowModel,
} from '@tanstack/react-table';
import React, { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { FooterCell } from './footer-cell';
import { TableCellValue } from './type';

interface IProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  newRow?: TData;
  setData: React.Dispatch<React.SetStateAction<TData[]>>;
  thClass?: string;
  tdClass?: string;
  preSaveValidation?: (newData: TData[], oldData: TData[]) => string;
  preSaveWarning?: (newData: TData[], oldData: TData[]) => string;
  onChangeCallback?: (newData: TData, oldData: TData) => void;
  onDeletedCallback?: (deletedData: TData) => void;
  onMultipleDeletedCallback?: (deletedData: TData[]) => void;
  enableRowSelection?: boolean;
  enableRemoveRow?: boolean;
  enableRowEditing?: boolean;
}

export default function DataTable<TData>({
  columns,
  data,
  newRow,
  setData,
  thClass,
  tdClass,
  preSaveValidation,
  preSaveWarning,
  onChangeCallback,
  onDeletedCallback,
  onMultipleDeletedCallback,
  enableRowSelection,
  enableRemoveRow = true,
  enableRowEditing = true,
}: IProps<TData>) {
  const [originalData, setOriginalData] = useState(() => [...data]);
  const [editedRows, setEditedRows] = useState({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [validRows, setValidRows] = useState<
    Record<string, Record<string, boolean>>
  >({});

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      rowSelection,
    },
    enableRowSelection: enableRowSelection ?? false,
    onRowSelectionChange: setRowSelection,
    meta: {
      editedRows,
      setEditedRows,
      validRows,
      setValidRows,
      enableRowSelection: enableRowSelection ?? false,
      enableRemoveRow,
      enableRowEditing,
      revertData: (rowIndex: number, revert: boolean) => {
        if (revert) {
          setData((old) =>
            old.map((row, index) =>
              index === rowIndex ? originalData[rowIndex] : row,
            ),
          );
        } else {
          const newData = data[rowIndex];
          const oldData = originalData[rowIndex];
          if (onChangeCallback) {
            onChangeCallback(newData, oldData);
          }
          setOriginalData((old) =>
            old.map((row, index) =>
              index === rowIndex ? data[rowIndex] : row,
            ),
          );
        }
      },
      updateData: (
        rowIndex: number,
        columnId: string,
        value: TableCellValue,
        isValid: boolean,
      ) => {
        setData((old) =>
          old.map((row, index) => {
            if (index === rowIndex) {
              return {
                ...old[rowIndex],
                [columnId]: value,
              };
            }
            return row;
          }),
        );

        // check duplicate vlanId
        if (columnId === 'vlanId') {
          const vlanIds = data.map((item: any) => {
            if (item.id === (data[rowIndex] as any).id) {
              return parseInt(value.toString(), 10);
            }
            return parseInt(item.vlanId.toString(), 10);
          });

          const duplicate = new Set(vlanIds).size !== vlanIds.length;

          setValidRows((old) => ({
            ...old,
            [rowIndex]: { ...old[rowIndex], vlanId: !duplicate && isValid },
          }));
          return;
        }
        setValidRows((old) => ({
          ...old,
          [rowIndex]: { ...old[rowIndex], [columnId]: isValid },
        }));
      },
      addRow: newRow
        ? () => {
            const setFunc = (old: TData[]) => [...old, newRow];
            setData(setFunc);
            setOriginalData(setFunc);
          }
        : undefined,
      removeRow: (rowIndex: number) => {
        const setFilterFunc = (old: TData[]) =>
          old.filter((_row: TData, index: number) => index !== rowIndex);
        setData(setFilterFunc);
        setOriginalData(setFilterFunc);
        onDeletedCallback?.(data[rowIndex]);
      },
      removeSelectedRows: (selectedRows: number[]) => {
        const setFilterFunc = (old: TData[]) =>
          old.filter((_row, index) => !selectedRows.includes(index));
        setData(setFilterFunc);
        setOriginalData(setFilterFunc);
        onMultipleDeletedCallback?.(selectedRows.map((index) => data[index]));
      },
      preSaveValidation: () => {
        if (preSaveValidation) {
          return preSaveValidation(data, originalData);
        }
        return '';
      },
      preSaveWarning: () => {
        if (preSaveWarning) {
          return preSaveWarning(data, originalData);
        }
        return '';
      },
    },
  });

  return (
    <table className="min-w-full divide-y divide-gray-300">
      <thead className="bg-gray-50">
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                colSpan={header.colSpan}
                scope="col"
                className={twMerge([
                  'p-1 text-center text-sm font-semibold text-gray-900',
                  thClass,
                ])}
              >
                {header.isPlaceholder ? null : (
                  <div>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </div>
                )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody className="divide-y divide-gray-200 bg-white">
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <td
                key={cell.id}
                data-testid={`table-cell-${cell.column.id}-${row.id}`}
                className={twMerge([
                  'whitespace-nowrap px-3 py-4 text-sm text-gray-900',
                  tdClass,
                ])}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
      {(enableRemoveRow || newRow || enableRowSelection) && (
        <tfoot className="bg-gray-50">
          <tr>
            <th colSpan={table.getCenterLeafColumns().length} align="right">
              <FooterCell table={table} />
            </th>
          </tr>
        </tfoot>
      )}
    </table>
  );
}
